import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { DataReviewerQuestionType } from '../../../../../redux/Slices/CMS/DataReviewerSlice';
import { useAppSelector } from '../../../../../redux/store/hooks';
import {
  useGetIsUnderReviewMutation,
  useUpdateQuestionMutation,
  useUpdateQuestionStatusMutation
} from '../../../../../services/dataReviewerApi/dataReviewerApi';
import { renderHtmlWithTeX } from '../../../../../utils/RenderQuestionWithTex';
import Button from '../../../../Common/Button';
import CommonDialog from '../../../../Common/Dialog';
import Accordion from '../../../../Common/Disclosure';
import 'react-quill/dist/quill.snow.css';
import CmsProgramming from '../../../../ReUsableComponents/CMS/CmsProgramming';
import EditPopUp from '../../../../ReUsableComponents/CMS/EditPopUp';
import Pagination from '../../../../ReUsableComponents/CMS/Pagination';

const HtmlRenderer = ({ content }: { content: string }) => {
  return <div dangerouslySetInnerHTML={{ __html: content }} />;
};

const RejectedTab = () => {
  const { domainData, moduleData, questionTypeData } = useAppSelector(
    state => state.root.questionBank
  );
  const {
    underReviewAndRejectedData,
    selectedSource,
    selectedSection,
    selectedTopic,
    selectedQuestionType,
    searchString
  } = useAppSelector(state => state.root.dataReviewer);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [comment, setComment] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQId, setSelectedQId] = useState('');
  const [updateQuestion] = useUpdateQuestionMutation();

  const [getIsUnderReview] = useGetIsUnderReviewMutation();
  const [updateQuestionStatus] = useUpdateQuestionStatusMutation();
  const [selectedQuestion, setSelectedQuestion] =
    useState<DataReviewerQuestionType | null>();

  const [questionText, setQuestionText] = useState('');
  const [readingPassage, setReadingPassage] = useState('');
  const [sourceText, setSourceText] = useState('');
  useEffect(() => {
    if (isModalOpen && selectedQuestion) {
      setQuestionText(selectedQuestion.questionText || '');
      setReadingPassage(selectedQuestion.readingPassage || '');
      setSourceText(selectedQuestion.sourceText || '');
    } else {
      setQuestionText('');
      setReadingPassage('');
      setSourceText('');
    }
  }, [isModalOpen, selectedQuestion, selectedQId]);
  const modules = {
    toolbar: [
      [{ header: '1' }, { header: '2' }, { font: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote', 'blockquote'],
      [{ color: [] }, { background: [] }], // Text color and background color
      [{ align: [] }],
      ['clean']
    ]
  };

  const formats = [
    'header',
    'font',
    'list',
    'bullet',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'color',
    'background',
    'align',
    'link',
    'image'
  ];

  useEffect(() => {
    getIsUnderReview({
      questionStatus: 4004,
      page: 0,
      size: 10,
      sourceId: selectedSource?.id ?? '',
      topicId: selectedTopic?.id ?? '',
      sectionId: selectedSection?.id ?? '',
      questionTypeId: selectedQuestionType?.id ?? '',
      searchString
    });
    setPageSize(10);
  }, [
    selectedSource,
    selectedSection,
    selectedTopic,
    selectedQuestionType,
    getIsUnderReview,
    searchString
  ]);

  const getQuestionNumber = (index: number) => {
    return index + 1;
  };
  const getName = (question: any) => {
    const selectedDomain = domainData?.data?.find(
      domain => domain.id === question.domain
    );
    const selectedModule = moduleData?.data?.find(
      module => module.id === question.moduleId
    );
    const selectedQuestionType = questionTypeData?.data?.find(
      type => type.id === question.questionType
    );

    return {
      domainName: selectedDomain?.dominName || '-',
      moduleName: selectedModule?.moduleName || '-',
      questionTypeName: selectedQuestionType?.questionTypeName || '-'
    };
  };
  const handlePageChange = (newPage: number) => {
    getIsUnderReview({
      questionStatus: 4004,
      page: newPage,
      size: pageSize,
      sourceId: selectedSource?.id ?? '',
      sectionId: selectedSection?.id ?? '',
      topicId: selectedTopic?.id ?? '',
      questionTypeId: selectedQuestionType?.id ?? '',
      searchString
    });
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    getIsUnderReview({
      questionStatus: 4004,
      page: 0,
      size: Number(event.target.value),
      sourceId: selectedSource?.id ?? '',
      sectionId: selectedSection?.id ?? '',
      topicId: selectedTopic?.id ?? '',
      questionTypeId: selectedQuestionType?.id ?? '',
      searchString
    });
    setPageSize(Number(event.target.value));
    setCurrentPage(0);
  };

  const validateForm = (question?: DataReviewerQuestionType) => {
    if (!question) return false;

    const baseRequiredFields = [
      'questionText',
      'examType',
      'questionType',
      'domain',
      'subdomain',
      'sourceFile',
      'sourceText',
      'correctAnswer'
    ];

    const mcqRequiredFields = ['optionA', 'optionB', 'optionC', 'optionD'];

    const fieldsToValidate =
      question.questionType === 'MCQ'
        ? [...baseRequiredFields, ...mcqRequiredFields]
        : baseRequiredFields;

    return fieldsToValidate.every(
      field => !!question[field as keyof DataReviewerQuestionType]
    );
  };

  const handleClickReview = async (id: string) => {
    try {
      const response = await updateQuestionStatus({
        questionId: id,
        status: 4002,
        comment: ''
      }).unwrap();
      toast.success('Question Status Updated successfully');
      getIsUnderReview({
        questionStatus: 4004,
        page: 0,
        size: 10,
        sourceId: selectedSource?.id ?? '',
        sectionId: selectedSection?.id ?? '',
        topicId: selectedTopic?.id ?? '',
        questionTypeId: selectedQuestionType?.id ?? '',
        searchString
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleClickEdit = (id: string, item: DataReviewerQuestionType) => {
    const itemCopy = JSON.parse(JSON.stringify(item));
    setIsModalOpen(true);
    setSelectedQId(id);
    setSelectedQuestion(itemCopy);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedQId('');
    setSelectedQuestion(null);
    setQuestionText('');
    setReadingPassage('');
    setSourceText('');
  };

  const handleSaveEdit = async (editedquestion: any) => {
    try {
      await updateQuestion({
        payload: editedquestion,
        questionId: selectedQId
      }).unwrap();

      toast.success('Question updated successfully');
      setIsModalOpen(false);
      getIsUnderReview({
        questionStatus: 4004,
        page: currentPage,
        size: pageSize,
        sourceId: selectedSource?.id ?? '',
        topicId: selectedTopic?.id ?? '',
        questionTypeId: selectedQuestionType?.id ?? '',
        sectionId: selectedSection?.id ?? '',
        searchString
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className='mb-32 h-full w-full overflow-y-auto pb-8'>
      {underReviewAndRejectedData?.data?.map((item, questionIndex) => (
        <div key={item.id} className='mr-4 px-6 py-2'>
          <Accordion
            width='w-full'
            title={`${questionIndex + 1}. ${item.questionText}`}
            isOpen
            onToggle={() => {
              // Handle accordion toggle logic here
            }}
          >
            <div>
              <div className='flex gap-2'>
                {getQuestionNumber(questionIndex)}.{' '}
                <HtmlRenderer content={item?.questionText} />
              </div>
              <HtmlRenderer content={item?.readingPassage} />
              <div className='flex w-full'>
                <p className='mt-3 w-1/2'>
                  <span className='font-bold'>ID</span>: {item?.id}
                </p>
                <p className='mt-3 w-1/2'>
                  <span className='font-bold'>Sub Topic</span>:{item?.subTopic}
                </p>
              </div>
              <p>
                <span className='font-bold'>Difficulty: </span>
                {item?.difficultyLevel}
              </p>
            </div>
            {!item?.isProgrammingQuestion && (
              <>
                <div className='mt-3'>
                  <span className='font-bold'>Options:</span>
                  {item?.options?.map((each: any, index: number) => (
                    <p key={each?.answerOption}>
                      <span className='font-bold'>{index + 1}: </span>
                      <span>
                        {renderHtmlWithTeX(each?.answerOption, false)}
                      </span>
                    </p>
                  ))}
                </div>
                <p className='my-2 text-green-600'>
                  <span className='font-bold'>Correct Answer:</span>{' '}
                  {renderHtmlWithTeX(item?.correctAnswer, false)}
                </p>
                <p className='my-2'>
                  <span className='font-bold'>Explanation:</span>{' '}
                  {renderHtmlWithTeX(item?.explanation, true)}
                </p>
                <div>
                  <p>
                    <span className='font-bold'>Created Date</span>:{' '}
                    {item?.createdDate}
                  </p>
                </div>
              </>
            )}
            {item?.isProgrammingQuestion && <CmsProgramming question={item} />}
            <div className='my-4 flex gap-2'>
              <Button
                handleClickBtn={() => handleClickReview(item.id)}
                label='Move to Review'
                variant='contained'
                classes='!mx-0'
              />
              <Button
                handleClickBtn={() => handleClickEdit(item.id, item)}
                label='Edit'
                variant='contained'
              />
            </div>
          </Accordion>
        </div>
      ))}
      {underReviewAndRejectedData?.data?.length > 0 && (
        <div
          style={{ width: 'calc(100% - 208px)' }}
          className='fixed bottom-0 flex justify-center border-t border-[#E3E5E9] bg-white shadow-md'
        >
          <Pagination
            totalquestions={underReviewAndRejectedData?.totalquestions}
            pageSize={pageSize}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
          />
        </div>
      )}
      <CommonDialog
        isOpen={isModalOpen}
        close={handleCloseModal}
        dialogTitle={'Edit Question'}
        childClasses='px-0 pb-0 pt-0'
        width='w-1/2 min-h-[250px] max-h-[600px] rounded-lg'
      >
        <div className='mb-4 max-h-[500px] overflow-y-auto rounded-lg px-6 pb-12 pt-4'>
          <EditPopUp
            selectedQuestion={selectedQuestion}
            handleSaveEdit={handleSaveEdit}
          />
          {/* <p className='my-2 block text-sm font-bold text-gray-700'>
            Question <span className='mr-2 text-[red]'>*</span>
          </p>
          <ReactQuill
            className='my-2 min-h-52 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50'
            placeholder='Question'
            value={questionText}
            onChange={setQuestionText}
            modules={{
              ...modules,
              clipboard: {
                matchVisual: false
              }
            }}
            formats={formats}
          />
          <p className='my-2 block text-sm font-bold text-gray-700'>
            Reading Passage <span className='mr-2 text-[red]'>*</span>
          </p>
          <ReactQuill
            className='my-2 min-h-52 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50'
            placeholder='Reading Passage'
            value={readingPassage}
            onChange={setReadingPassage}
            modules={modules}
            formats={formats}
          />
          <TextField
            isRequired
            label='Exam Type'
            handleInputChange={e => {
              setSelectedQuestion(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  examType: e.target.value
                };
              });
            }}
            placeholder='Enter your comment'
            type='text'
            value={selectedQuestion?.examType ?? ''}
          />
          <TextField
            isRequired
            label='Question Type'
            handleInputChange={e => {
              setSelectedQuestion(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  questionType: e.target.value
                };
              });
            }}
            placeholder='Enter your comment'
            type='text'
            value={selectedQuestion?.questionType ?? ''}
          />
          <TextField
            isRequired
            label='Domain'
            handleInputChange={e => {
              setSelectedQuestion(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  domain: e.target.value
                };
              });
            }}
            placeholder='Enter your Domain'
            type='text'
            value={selectedQuestion?.domain ?? ''}
          />
          <TextField
            isRequired
            label='Sub Domain'
            handleInputChange={e => {
              setSelectedQuestion(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  subTopic: e.target.value
                };
              });
            }}
            placeholder='Enter your Sub Domain'
            type='text'
            value={selectedQuestion?.subTopic ?? ''}
          />
          <TextField
            isRequired
            label='Module'
            handleInputChange={e => {
              setSelectedQuestion(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  module: e.target.value
                };
              });
            }}
            placeholder='Enter your Module'
            type='text'
            value={selectedQuestion?.module ?? ''}
          />
          <TextField
            isRequired
            label='Source File'
            handleInputChange={e => {
              setSelectedQuestion(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  sourceFile: e.target.value
                };
              });
            }}
            placeholder='Enter your Source File'
            type='text'
            value={selectedQuestion?.sourceFile ?? ''}
          />
          <p className='my-2 block text-sm font-bold text-gray-700'>
            Source Text <span className='mr-2 text-[red]'>*</span>
          </p>
          <ReactQuill
            className='my-2 min-h-52 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50'
            placeholder='sourceText'
            value={sourceText}
            onChange={setSourceText}
            modules={modules}
            formats={formats}
          />
          {selectedQuestion?.questionType === 'MCQ' && (
            <div>
              <TextField
                isRequired
                label='Option A'
                handleInputChange={e => {
                  setSelectedQuestion(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      optionA: e.target.value
                    };
                  });
                }}
                placeholder='Enter Option A'
                type='text'
                value={selectedQuestion?.optionA ?? ''}
              />
              <TextField
                isRequired
                label='Option B'
                handleInputChange={e => {
                  setSelectedQuestion(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      optionB: e.target.value
                    };
                  });
                }}
                placeholder='Enter Option B'
                type='text'
                value={selectedQuestion?.optionB ?? ''}
              />
              <TextField
                isRequired
                label='Option C'
                handleInputChange={e => {
                  setSelectedQuestion(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      optionC: e.target.value
                    };
                  });
                }}
                placeholder='Enter Option C'
                type='text'
                value={selectedQuestion?.optionC ?? ''}
              />
              <TextField
                isRequired
                label='Option D'
                handleInputChange={e => {
                  setSelectedQuestion(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      optionD: e.target.value
                    };
                  });
                }}
                placeholder='Enter Option D'
                type='text'
                value={selectedQuestion?.optionD ?? ''}
              />
            </div>
          )}
          <TextField
            isRequired
            label='Correct Answer'
            handleInputChange={e => {
              setSelectedQuestion(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  correctAnswer: e.target.value
                };
              });
            }}
            placeholder='Enter Correct Answer'
            type='text'
            value={selectedQuestion?.correctAnswer ?? ''}
          />
          <div className='fixed bottom-0 right-0 flex w-full items-center justify-end bg-white px-6 py-2'>
            <Button
              disabled={!validateForm(selectedQuestion || undefined)}
              label='Submit'
              variant='contained'
              handleClickBtn={async () => {
                try {
                  const response = await updateQuestion({
                    payload: selectedQuestion,
                    questionId: selectedQId
                  }).unwrap();
                  setIsModalOpen(false);
                  toast.success('Question updated successfully');
                  getIsUnderReview({
                    questionStatus: 4004,
                    page: currentPage,
                    size: pageSize,
                    sourceId: selectedSource?.id ?? '',
                    sectionId: selectedSection?.id ?? '',
                    topicId: selectedTopic?.id ?? '',
                    questionTypeId: selectedQuestionType?.id ?? ''
                  });
                } catch (err) {
                  console.log(err);
                }
              }}
            />
            <Button
              label='Cancel'
              variant='outlined'
              handleClickBtn={() => setIsModalOpen(false)}
            />
          </div> */}
        </div>
      </CommonDialog>
      {underReviewAndRejectedData?.data?.length === 0 && (
        <div className='mt-8 flex h-full items-center justify-center'>
          <p>No Data Found</p>
        </div>
      )}
    </div>
  );
};

export default RejectedTab;
